import { FONT_FAMILY } from "@excalidraw/common";

import type { ExcalidrawTextElement } from "@excalidraw/element/types";

interface FontFallbackMapping {
  [key: number]: number; // fontFamily -> fallback fontFamily
}

interface FontErrorNotification {
  type: "warning" | "error";
  message: string;
  fontFamily: number;
  timestamp: number;
}

/**
 * Handles font loading errors with graceful fallbacks and user notifications
 */
export class FontErrorHandler {
  private static instance: FontErrorHandler | null = null;
  private notifications: FontErrorNotification[] = [];
  private readonly maxNotifications = 10;

  // Define fallback mappings for fonts
  private readonly fallbackMappings: FontFallbackMapping = {
    [FONT_FAMILY.Inter]: FONT_FAMILY.Helvetica,
    [FONT_FAMILY.Roboto]: FONT_FAMILY.Helvetica,
    [FONT_FAMILY["Open Sans"]]: FONT_FAMILY.Helvetica,
    [FONT_FAMILY.Nunito]: FONT_FAMILY.Helvetica,
    [FONT_FAMILY["Lilita One"]]: FONT_FAMILY.Virgil,
    [FONT_FAMILY["Comic Shanns"]]: FONT_FAMILY.Virgil,
    [FONT_FAMILY.Cascadia]: FONT_FAMILY.Helvetica,
    [FONT_FAMILY["Liberation Sans"]]: FONT_FAMILY.Helvetica,
    // Ultimate fallback to Excalifont for any unmapped fonts
  };

  private constructor() {}

  public static getInstance(): FontErrorHandler {
    if (!FontErrorHandler.instance) {
      FontErrorHandler.instance = new FontErrorHandler();
    }
    return FontErrorHandler.instance;
  }

  /**
   * Handle font loading failure and return appropriate fallback
   */
  public handleFontLoadingError(
    fontFamily: number,
    error: Error,
    context: "loading" | "rendering" = "loading",
  ): number {
    const fallbackFont = this.getFallbackFont(fontFamily);

    // Create notification
    const notification: FontErrorNotification = {
      type: context === "loading" ? "warning" : "error",
      message: this.createErrorMessage(fontFamily, error, fallbackFont),
      fontFamily,
      timestamp: Date.now(),
    };

    this.addNotification(notification);

    // Log error for debugging
    console.warn(
      `Font loading failed for ${this.getFontName(
        fontFamily,
      )}, falling back to ${this.getFontName(fallbackFont)}:`,
      error,
    );

    return fallbackFont;
  }

  /**
   * Get appropriate fallback font for a given font family
   */
  private getFallbackFont(fontFamily: number): number {
    // Check if we have a specific fallback mapping
    if (this.fallbackMappings[fontFamily]) {
      return this.fallbackMappings[fontFamily];
    }

    // Default fallback hierarchy
    const fallbackHierarchy = [
      FONT_FAMILY.Helvetica,
      FONT_FAMILY.Virgil,
      FONT_FAMILY.Excalifont,
    ];

    // Return the first fallback that's not the same as the failed font
    for (const fallback of fallbackHierarchy) {
      if (fallback !== fontFamily) {
        return fallback;
      }
    }

    // Ultimate fallback
    return FONT_FAMILY.Excalifont;
  }

  /**
   * Create user-friendly error message
   */
  private createErrorMessage(
    fontFamily: number,
    error: Error,
    fallbackFont: number,
  ): string {
    const fontName = this.getFontName(fontFamily);
    const fallbackName = this.getFontName(fallbackFont);

    if (error.message.includes("network")) {
      return `Unable to load ${fontName} due to network issues. Using ${fallbackName} instead.`;
    }

    if (error.message.includes("timeout")) {
      return `${fontName} took too long to load. Using ${fallbackName} instead.`;
    }

    return `Failed to load ${fontName}. Using ${fallbackName} instead.`;
  }

  /**
   * Get font name from font family number
   */
  private getFontName(fontFamily: number): string {
    const entry = Object.entries(FONT_FAMILY).find(
      ([, value]) => value === fontFamily,
    );
    return entry ? entry[0] : "Unknown Font";
  }

  /**
   * Add notification to the queue
   */
  private addNotification(notification: FontErrorNotification): void {
    this.notifications.push(notification);

    // Keep only the most recent notifications
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(-this.maxNotifications);
    }
  }

  /**
   * Get recent notifications
   */
  public getNotifications(): readonly FontErrorNotification[] {
    return [...this.notifications];
  }

  /**
   * Clear all notifications
   */
  public clearNotifications(): void {
    this.notifications = [];
  }

  /**
   * Check if a font family is safe to use (has loaded successfully before)
   */
  public isFontSafe(fontFamily: number): boolean {
    // Check if this font has recent failures
    const recentFailures = this.notifications.filter(
      (notification) =>
        notification.fontFamily === fontFamily &&
        Date.now() - notification.timestamp < 5 * 60 * 1000, // 5 minutes
    );

    return recentFailures.length === 0;
  }

  /**
   * Validate and potentially fix font family for text elements
   */
  public validateFontFamily(fontFamily: number): number {
    if (!this.isFontSafe(fontFamily)) {
      const fallbackFont = this.getFallbackFont(fontFamily);
      // Mark the fallback font as safe since we're using it as a fallback
      this.markFontAsSafe(fallbackFont);
      return fallbackFont;
    }
    return fontFamily;
  }

  /**
   * Mark a font as safe to use (remove recent failures)
   */
  private markFontAsSafe(fontFamily: number): void {
    // Remove recent failures for this font to mark it as safe
    this.notifications = this.notifications.filter(
      (notification) => notification.fontFamily !== fontFamily,
    );
  }

  /**
   * Handle system font fallback when all else fails
   */
  public getSystemFontFallback(): string {
    // Return CSS font stack for system fonts
    return 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  }
}
